import { InteractionRequiredAuthError } from "@azure/msal-browser";

export const getAccessToken = () => {
  return localStorage.getItem("token");
};

/**
 * Decodes a JWT token and returns the payload
 */
export const decodeJWT = (token: string): any => {
  try {
    const base64Url = token.split(".")[1];
    if (!base64Url) return null;
    
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error("Error decoding JWT:", error);
    return null;
  }
};

/**
 * Determines the authentication type based on the token's issuer claim
 * @param token - The JWT token to check
 * @returns 'manual' | 'microsoft' | null
 */
export const getAuthType = (token: string | null): "manual" | "microsoft" | null => {
  if (!token) return null;
  
  const decoded = decodeJWT(token);
  if (!decoded || !decoded.iss) return null;
  
  if (decoded.iss === "manual-auth") {
    return "manual";
  } else if (decoded.iss && decoded.iss.includes("microsoft")) {
    return "microsoft";
  }
  
  return null;
};

export const debounceFn = (func: (searchKey: any) => void, delay: number) => {
  let timer: NodeJS.Timeout;
  return function innerFn(this: any, ...args: any) {
    clearTimeout(timer);
    timer = setTimeout(() => {
      func.apply(this, args);
    }, delay);
  };
};

export const debounce = (func: () => void, delay: number) => {
  let timeout: NodeJS.Timeout;
  return (...args: any) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(this, args), delay);
  };
};

export const handleSilentLogout = (instance: any) => {
  const idToken = localStorage.getItem("idToken");
  const authType = getAuthType(idToken);

  // If it's Microsoft auth and instance is available, use MSAL logout
  if (authType === "microsoft" && instance) {
    // Set flag to prevent login page flash
    sessionStorage.setItem("msal_logout_in_progress", "true");
    
    instance
      .logout({
        onRedirectNavigate: () => false,
      })
      .then(() => {
        // Clear storage after logout
        localStorage.clear();
        sessionStorage.clear();
        window.location.href = "/#/";
      })
      .catch(() => {
        // If logout fails, clear and redirect manually
        sessionStorage.removeItem("msal_logout_in_progress");
        localStorage.clear();
        sessionStorage.clear();
        window.location.href = "/#/";
      });
  } else {
    // For manual auth or if no instance, clear and redirect
    localStorage.clear();
    sessionStorage.clear();
    window.location.href = "/#/";
  }
};

/**
 * Acquires token silently for Microsoft authentication only
 * This function should only be used for Microsoft auth flow
 */
export const silentTokenAcquisition = async (instance: any, account: any) => {
  try {
    const response = await instance.acquireTokenSilent({
      account,
      scopes: ["openid", "profile"],
    });
    if (response?.idToken) {
      localStorage.setItem("idToken", response.idToken);
      return response.idToken;
    }
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) {
      try {
        const response = await instance.acquireTokenPopup({
          account,
          scopes: ["openid", "profile"],
        });
        if (response?.idToken) {
          localStorage.setItem("idToken", response.idToken);
          return response.idToken;
        }
      } catch (popupError) {
        console.error("Failed to acquire token via popup:", popupError);
        handleSilentLogout(instance);
        return null;
      }
    } else {
      handleSilentLogout(instance);
      return null;
    }
  }
  return null;
};
